
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import type { PEOProvider } from '@/types/peo.types';

/**
 * Hook that provides the function to fetch PEO providers from Supabase
 */
export const useFetchProviders = () => {
  const { toast } = useToast();

  const fetchProviders = useCallback(async () => {
    console.log('Fetching PEO providers from Supabase...');
    
    // Define diagnosticInfo at the outer level so it's accessible in both try and catch blocks
    const diagnosticInfo: any = {
      timestamp: new Date().toISOString(),
      checks: []
    };
    
    try {
      // Add diagnostic information
      console.log('Starting Supabase query to peo_providers table');
      
      // Try to get the currently authenticated user
      try {
        const { data: authData, error: authError } = await supabase.auth.getUser();
        diagnosticInfo.checks.push({
          check: 'auth_user',
          result: authError ? 'error' : 'success',
          error: authError ? authError.message : null,
          userId: authData?.user?.id || null
        });
        
        if (authData?.user) {
          console.log("Current user ID:", authData.user.id);
        } else {
          console.warn("No authenticated user found");
        }
      } catch (authErr) {
        console.error("Error checking auth:", authErr);
      }
      
      // Now fetch the PEO providers
      const { data, error } = await supabase
        .from('peo_providers')
        .select('*')
        .order('name');
      
      diagnosticInfo.checks.push({
        check: 'peo_providers',
        result: error ? 'error' : 'success',
        error: error ? error.message : null,
        count: data ? data.length : null
      });
      
      if (error) {
        console.error("Error fetching PEO providers:", error);
        
        // Log more details about the error
        console.error("Error code:", error.code);
        console.error("Error message:", error.message);
        console.error("Error details:", error.details);
        
        // Check for specific error types
        if (error.message.includes("permission denied")) {
          toast({
            title: 'Permission Error',
            description: 'You do not have permission to access PEO providers. Please check your access rights.',
            variant: 'destructive',
          });
          throw new Error(`Permission denied: ${error.message}`);
        } else {
          toast({
            title: 'Error',
            description: 'Failed to load PEO providers: ' + error.message,
            variant: 'destructive',
          });
          throw error;
        }
      }
      
      console.log(`Successfully fetched ${data?.length || 0} PEO providers`);
      
      // Log the first provider for diagnostic purposes (without sensitive data)
      if (data && data.length > 0) {
        console.log('Sample provider:', {
          id: data[0].id,
          name: data[0].name,
          employee_range: data[0].employee_range
        });
        
        // Log structure of data to identify potential issues
        console.log('Provider data structure (first item):', 
          Object.keys(data[0]).reduce((acc, key) => {
            acc[key] = typeof data[0][key];
            return acc;
          }, {} as Record<string, string>)
        );
      }
      
      // Cast to PEOProvider type
      const typedData = data as unknown as PEOProvider[];
      
      return { data: typedData, diagnosticInfo: diagnosticInfo };
    } catch (err) {
      console.error("Unexpected error fetching PEO providers:", err);
      
      // Improve error logging with more details
      if (err instanceof Error) {
        console.error("Error name:", err.name);
        console.error("Error message:", err.message);
        console.error("Error stack:", err.stack);
      }
      
      // Since diagnosticInfo is now defined at the outer level, we can use it here
      throw { error: err, diagnosticInfo: diagnosticInfo };
    }
  }, [toast]);

  return { fetchProviders };
};
